import React, { useState } from 'react';
import { AlertTriangle, Camera, Loader2, MessageSquare, Plus, Send, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';
import { listingReviewAPI } from '../../lib/api';
import { toast } from 'sonner';

const FIELD_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'description', label: 'Description' },
  { value: 'location_id', label: 'Location' },
  { value: 'bedrooms', label: 'Bedrooms' },
  { value: 'bathrooms', label: 'Bathrooms' },
  { value: 'sqm', label: 'Square meters' },
  { value: 'base_amount', label: 'Price / rent' },
  { value: 'pet_policy', label: 'Pet policy' },
  { value: 'amenities', label: 'Amenities' },
];

const PHOTO_REASONS = [
  { value: 'inappropriate_content', label: 'Inappropriate or explicit content' },
  { value: 'copyright_concern', label: 'Copyright / stolen image concern' },
  { value: 'personal_information_visible', label: 'Personal information visible' },
  { value: 'sensitive_location_exposed', label: 'Address or sensitive location exposed' },
  { value: 'low_quality_image', label: 'Low quality / unusable image' },
  { value: 'misleading_or_unrelated', label: 'Misleading or unrelated image' },
  { value: 'duplicate_image', label: 'Duplicate image' },
  { value: 'watermark_or_advertisement', label: 'Watermark / advertisement' },
  { value: 'external_contact_or_link', label: 'External link or contact info shown' },
  { value: 'safety_or_policy_concern', label: 'Safety or policy concern' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_NOTE = {
  target_type: 'field',
  target_field: 'description',
  target_photo_id: '',
  selected_text: '',
  comment: '',
  severity: 'needs_change',
  reason_code: '',
  remove_photo: false,
};

function noteLabel(note) {
  if (note.target_type === 'photo') return `Photo${note.remove_photo ? ' removal' : ' note'}`;
  if (note.target_type === 'listing') return 'General listing note';
  return FIELD_OPTIONS.find((item) => item.value === note.target_field)?.label || note.target_field;
}

function severityTone(severity) {
  if (severity === 'policy_issue') return 'bg-rose-50 text-rose-700 border-rose-200';
  if (severity === 'needs_change') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-sky-50 text-sky-700 border-sky-200';
}

export default function ListingReviewBatchModal({ listing, open, onClose, onSent }) {
  const { t } = useLanguage();
  const [summary, setSummary] = useState('');
  const [draft, setDraft] = useState(DEFAULT_NOTE);
  const [notes, setNotes] = useState([]);
  const [sending, setSending] = useState(false);

  if (!open || !listing) return null;

  const activePhotos = (listing.photos || []).filter((photo) => photo.moderation_status !== 'removed');

  const resetDraft = () => setDraft(DEFAULT_NOTE);

  const addNote = () => {
    const comment = draft.comment.trim();
    if (comment.length < 3) {
      toast.error('Add a clear comment before adding the review note.');
      return;
    }
    if (draft.target_type === 'field' && !draft.target_field) {
      toast.error('Choose the field this comment applies to.');
      return;
    }
    if (draft.target_type === 'photo' && !draft.target_photo_id) {
      toast.error('Choose the photo this comment applies to.');
      return;
    }
    if (draft.target_type === 'photo' && draft.remove_photo && !draft.reason_code) {
      toast.error('Choose why the photo is being removed.');
      return;
    }
    setNotes((prev) => [...prev, { ...draft, comment }]);
    resetDraft();
  };

  const removeNote = (index) => {
    setNotes((prev) => prev.filter((_, i) => i !== index));
  };

  const sendBatch = async () => {
    if (notes.length === 0) {
      toast.error('Add at least one review note before sending.');
      return;
    }
    setSending(true);
    try {
      await listingReviewAPI.sendBatch(listing.id, {
        summary: summary.trim() || undefined,
        annotations: notes,
      });
      toast.success(`Sent ${notes.length} review note${notes.length !== 1 ? 's' : ''} as one owner notification.`);
      onSent?.();
      onClose?.();
      setSummary('');
      setNotes([]);
      resetDraft();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send review batch.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4" data-testid="listing-review-batch-modal">
      <div className="w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white/95 backdrop-blur px-7 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.review_modal.workflow_label', 'Owner review workflow')}
            </p>
            <h2 className="mt-1 font-heading text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-500" /> {t('admin.review_modal.title', 'Review notes')}
            </h2>
            <p className="text-sm text-slate-700 mt-1">{listing.title}</p>
            <p className="text-xs text-slate-500 mt-1.5">{t('admin.review_modal.listing_hint', 'Add all comments first. The owner receives one consolidated notification when you send.')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 p-7">
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 text-sm font-medium">{t('admin.review_modal.summary_label', 'Optional review summary')}</Label>
              <Textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="mt-1.5 rounded-lg bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-0"
                placeholder={t('admin.review_modal.summary_placeholder', 'Overall note to the owner…')}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3.5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">{t('admin.review_modal.add_note_section', 'Add review note')}</h3>
                <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Draft</Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.target_label', 'Target')}</Label>
                  <select
                    value={draft.target_type}
                    onChange={(e) => setDraft({ ...DEFAULT_NOTE, target_type: e.target.value })}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value="field">{t('admin.review_modal.target_field_opt', 'Field/text')}</option>
                    <option value="photo">{t('admin.review_modal.target_photo_opt', 'Photo')}</option>
                    <option value="listing">{t('admin.review_modal.target_general_opt', 'General')}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.severity_label', 'Severity')}</Label>
                  <select
                    value={draft.severity}
                    onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value="info">{t('admin.review_modal.severity_info', 'Info')}</option>
                    <option value="needs_change">{t('admin.review_modal.severity_needs_change', 'Needs change')}</option>
                    <option value="policy_issue">{t('admin.review_modal.severity_policy_issue', 'Policy issue')}</option>
                  </select>
                </div>
                {draft.target_type === 'field' && (
                  <div>
                    <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.field_label', 'Field')}</Label>
                    <select
                      value={draft.target_field}
                      onChange={(e) => setDraft({ ...draft, target_field: e.target.value })}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      {FIELD_OPTIONS.map((field) => <option key={field.value} value={field.value}>{field.label}</option>)}
                    </select>
                  </div>
                )}
                {draft.target_type !== 'field' && (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-white/70 px-3 py-2 text-xs text-slate-400 flex items-center">
                    {draft.target_type === 'photo'
                      ? t('admin.review_modal.field_not_needed_photo', 'Field is not required for photo notes')
                      : t('admin.review_modal.field_not_needed_general', 'Field is not required for general notes')}
                  </div>
                )}
              </div>

              {draft.target_type === 'field' && (
                <div>
                  <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.excerpt_label', 'Highlighted text / excerpt')}</Label>
                  <Input
                    value={draft.selected_text}
                    onChange={(e) => setDraft({ ...draft, selected_text: e.target.value })}
                    placeholder={t('admin.review_modal.excerpt_placeholder', 'Paste the exact text or section you want the owner to see highlighted')}
                    className="mt-1.5 rounded-lg bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-0"
                  />
                </div>
              )}

              {draft.target_type === 'photo' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.photo_label', 'Photo')}</Label>
                    <select
                      value={draft.target_photo_id}
                      onChange={(e) => setDraft({ ...draft, target_photo_id: e.target.value })}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <option value="">{t('admin.review_modal.photo_select_placeholder', 'Select photo…')}</option>
                      {activePhotos.map((photo, index) => (
                        <option key={photo.id} value={photo.id}>
                          Photo {index + 1} — {photo.category || 'General'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={draft.remove_photo}
                      onChange={(e) => setDraft({ ...draft, remove_photo: e.target.checked, severity: e.target.checked ? 'policy_issue' : draft.severity })}
                    />
                    {t('admin.review_modal.remove_photo_label', 'Remove/hide this photo from active listing photos')}
                  </label>
                  {draft.remove_photo && (
                    <div>
                      <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.removal_reason_label', 'Removal reason')}</Label>
                      <select
                        value={draft.reason_code}
                        onChange={(e) => setDraft({ ...draft, reason_code: e.target.value })}
                        className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      >
                        <option value="">{t('admin.review_modal.removal_reason_select', 'Choose reason…')}</option>
                        {PHOTO_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-slate-700 text-xs font-medium">{t('admin.review_modal.comment_label', 'Admin comment')}</Label>
                <Textarea
                  value={draft.comment}
                  onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                  rows={4}
                  className="mt-1.5 rounded-lg bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-0"
                  placeholder={t('admin.review_modal.comment_placeholder', 'Tell the owner what needs attention. Do not rewrite the listing for them.')}
                />
              </div>

              <Button onClick={addNote} className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                <Plus className="w-4 h-4 mr-2" /> {t('admin.review_modal.add_note_btn', 'Add note to batch')}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="font-semibold text-slate-800 mb-2">{t('admin.review_modal.notes_to_send_heading', 'Notes to send')}</h3>
              {notes.length === 0 ? (
                <div className="text-center py-10 px-4 text-slate-500 rounded-lg border border-dashed border-slate-200 bg-slate-50/70">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-600">{t('admin.review_modal.no_notes_title', 'No notes added yet')}</p>
                  <p className="text-xs mt-1">{t('admin.review_modal.no_notes_hint', 'Add notes on the left. The owner will receive one combined notification.')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map((note, index) => (
                    <div key={`${note.target_type}-${index}`} className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">{noteLabel(note)}</Badge>
                            <Badge className={`border text-xs ${severityTone(note.severity)}`}>{note.severity.replace('_', ' ')}</Badge>
                            {note.remove_photo && <Badge className="bg-red-50 text-red-700 border border-red-200 text-xs">{t('admin.review_modal.remove_badge', 'remove photo')}</Badge>}
                          </div>
                          {note.selected_text && <p className="text-xs text-slate-500 mb-1 italic">"{note.selected_text}"</p>}
                          <p className="text-sm leading-relaxed text-slate-700">{note.comment}</p>
                        </div>
                        <button
                          onClick={() => removeNote(index)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                          aria-label={t('admin.review_modal.remove_note_btn', 'Remove note')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {activePhotos.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> {t('admin.review_modal.photos_section', 'Listing photos')}
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {activePhotos.slice(0, 9).map((photo, index) => (
                    <button
                      key={photo.id}
                      type="button"
                      onClick={() => setDraft((prev) => ({ ...prev, target_type: 'photo', target_photo_id: photo.id }))}
                      className={`text-left rounded-lg overflow-hidden border transition focus:outline-none focus:ring-2 focus:ring-amber-200 ${
                        draft.target_photo_id === photo.id
                          ? 'border-amber-400 ring-2 ring-amber-200'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <img src={photo.url} alt={`Photo ${index + 1}`} className="w-full h-20 object-cover" />
                      <div className="px-2 py-1 bg-white text-[11px] text-slate-600 border-t border-slate-100">Photo {index + 1}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p>{t('admin.review_modal.consolidation_warning', 'Nothing is sent until you click Send consolidated review. This prevents spammy per-comment notifications.')}</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-7 py-4">
          <p className="text-xs text-slate-500">
            {notes.length === 0
              ? t('admin.review_modal.send_disabled_hint', 'Add at least one note to enable sending.')
              : t('admin.review_modal.send_ready_hint', 'Ready to send one consolidated owner notification.')}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-700">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={sendBatch}
              disabled={sending || notes.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {t('admin.review_modal.send_btn', 'Send consolidated review')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
